// Path: C:\gestion-copro\src\pages\vigie\CalendrierVigiePage.tsx
// Page Calendrier / Échéances VIGIE
// Session 130 : Création — Vue mensuelle, échéances auto + manuelles, modal CRUD
// Session 130b : Ajout aide contextuelle 3 niveaux (accueil riche + compact permanent + tooltips)
// Session 179 : Toggle résolu transversal — disponible sur TOUTES les échéances (y compris automatiques)
// Session 180 : Vue urgences (?vue=urgences) — affichage cross-mois des échéances critiques
//               Bouton "Relancer via SENTINELLE" sur les échéances courrier
//               Renommage "Mon calendrier" → "Mes échéances" (titre page dynamique)
//               Suppression bouton "Terminée" (doublon avec "Clore") — Clore/Rouvrir = action unique
//               Fix compteurs : "À surveiller" inclut >30j sans plafond (total cohérent)
// Grille d'urgence alignée sur le Principe 9 de SENTINELLE
// Session 285 : Refonte UX — Frise 6 mois glissants + liste prioritaire (dépassées + <30j)
//               Suppression navigation mois par mois — vue temporelle globale
//               Toutes sources affichées sur la même frise (courriers, garanties, dossiers, manuelles)

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  Calendar,
  Plus,
  Loader2,
  Shield,
  Mail,
  FolderOpen,
  Clock,
  CheckCircle,
  AlertTriangle,
  X,
  Trash2,
  Edit3,
  ExternalLink,
  Filter,
  CheckCheck,
  MessageSquare,
  Sparkles,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import {
  chargerToutesEcheances,
  creerEcheance,
  modifierEcheance,
  supprimerEcheance,
} from '../../services/echeance.service';
import {
  Echeance,
  EcheanceSource,
  EcheanceCategorieManuelle,
  ECHEANCE_CATEGORIES_MANUELLES,
  ECHEANCE_URGENCE_CONFIG,
  calculerJoursRestants,
  calculerUrgence,
} from '../../types/echeance.types';

// ============================================
// CONSTANTES
// ============================================

const MOIS_NOMS_COURTS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const SOURCE_CONFIG: Record<EcheanceSource, { label: string; icon: React.ReactNode }> = {
  garantie: { label: 'Garantie', icon: <Shield className="w-3 h-3" /> },
  courrier: { label: 'Courrier', icon: <Mail className="w-3 h-3" /> },
  dossier: { label: 'Dossier', icon: <FolderOpen className="w-3 h-3" /> },
  manuelle: { label: 'Manuel', icon: <Clock className="w-3 h-3" /> },
};

// ============================================
// UTILITAIRES
// ============================================

const formatJours = (jours: number): string => {
  if (jours === 0) return "Aujourd'hui";
  if (jours === 1) return 'Demain';
  if (jours === -1) return 'Hier';
  if (jours > 0) return `J-${jours}`;
  return `J+${Math.abs(jours)}`;
};

const getDateEcheance = (ech: Echeance): Date =>
  ech.dateEcheance?.toDate?.() || new Date(ech.dateEcheance as any);

const getChipClasses = (joursRestants: number): string => {
  if (joursRestants < 0) return 'bg-red-50 border-red-200 text-red-800 hover:bg-red-100';
  if (joursRestants <= 30) return 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100';
  return 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50';
};

const getDotClass = (joursRestants: number, resolu: boolean): string => {
  if (resolu) return 'bg-gray-300';
  if (joursRestants < 0) return 'bg-red-500';
  if (joursRestants <= 30) return 'bg-amber-500';
  if (joursRestants <= 90) return 'bg-blue-400';
  return 'bg-gray-300';
};

const getBadgeClasses = (source: EcheanceSource): string => {
  const map: Record<EcheanceSource, string> = {
    garantie: 'bg-blue-50 text-blue-700',
    courrier: 'bg-amber-50 text-amber-700',
    dossier: 'bg-purple-50 text-purple-700',
    manuelle: 'bg-gray-50 text-gray-600',
  };
  return map[source];
};

// ============================================
// COMPOSANT : AIDE CONTEXTUELLE — PREMIÈRE VISITE
// ============================================

const AideAccueil: React.FC<{ onFermer: () => void }> = ({ onFermer }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
    <div className="p-5 pb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <Sparkles className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Bienvenue dans tes échéances</h2>
            <p className="text-sm text-blue-700">Ne rate plus aucun délai critique de ta copropriété</p>
          </div>
        </div>
        <button onClick={onFermer} className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors flex-shrink-0">
          <X className="w-4 h-4 text-blue-400" />
        </button>
      </div>
    </div>
    <div className="px-5 pb-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white/70 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1.5">Pourquoi ce calendrier ?</p>
          <p className="text-sm text-gray-700">
            Rater une échéance peut coûter cher : garantie expirée, mise en demeure ignorée, AG mal préparée.
            La frise centralise tous tes délais sur 6 mois d'un seul regard.
          </p>
        </div>
        <div className="bg-white/70 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1.5">Comment ça marche ?</p>
          <div className="text-sm text-gray-700 space-y-1">
            <p className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span><strong>Automatique :</strong> garanties, courriers sans réponse, dossiers avec deadline.</span>
            </p>
            <p className="flex items-start gap-2">
              <Plus className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span><strong>Manuel :</strong> AG, contrat syndic, appels de fonds, assurance...</span>
            </p>
          </div>
        </div>
        <div className="bg-white/70 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1.5">Code couleur</p>
          <div className="space-y-1 text-sm text-gray-700">
            <p className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />Dépassé ou critique</p>
            <p className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0" />Dans les 30 jours</p>
            <p className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-blue-400 flex-shrink-0" />Planifié</p>
          </div>
        </div>
      </div>
      <div className="mt-3 text-center">
        <button
          onClick={onFermer}
          className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <CheckCircle className="w-4 h-4" />
          {"J'ai compris, c'est parti"}
        </button>
      </div>
    </div>
  </div>
);

const AideCompacte: React.FC<{ onVoirPlus: () => void }> = ({ onVoirPlus }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
    <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
      <Calendar className="w-4 h-4 text-blue-600" />
    </div>
    <p className="text-sm text-blue-800 flex-1">
      <strong>Mes échéances</strong> — Frise 6 mois, toutes sources confondues. La liste ci-dessous affiche ce qui est dépassé ou dans les 30 prochains jours.
    </p>
    <button
      onClick={onVoirPlus}
      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
    >
      <BookOpen className="w-3.5 h-3.5" />
      En savoir plus
    </button>
  </div>
);

// ============================================
// COMPOSANT : FRISE 6 MOIS
// ============================================

interface Frise6MoisProps {
  echeances: Echeance[];
  onChipClick: (ech: Echeance) => void;
}

const Frise6Mois: React.FC<Frise6MoisProps> = ({ echeances, onChipClick }) => {
  const aujourd = new Date();

  const mois6 = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(aujourd.getFullYear(), aujourd.getMonth() + i, 1);
      return { annee: d.getFullYear(), mois: d.getMonth() };
    });
  }, []);

  const echeancesParMois = useMemo(() => {
    const groupes: Record<string, Echeance[]> = {};
    for (const { annee, mois } of mois6) {
      groupes[`${annee}-${mois}`] = [];
    }
    for (const ech of echeances) {
      if (ech.terminee || ech.resolu) continue;
      const d = getDateEcheance(ech);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (groupes[key] !== undefined) {
        groupes[key].push(ech);
      }
    }
    for (const key of Object.keys(groupes)) {
      groupes[key].sort((a, b) => (a.joursRestants ?? 999) - (b.joursRestants ?? 999));
    }
    return groupes;
  }, [echeances, mois6]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
      <div className="grid gap-3 min-w-[600px]" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
        {mois6.map(({ annee, mois }) => {
          const isCurrent = annee === aujourd.getFullYear() && mois === aujourd.getMonth();
          const chips = echeancesParMois[`${annee}-${mois}`] || [];
          return (
            <div key={`${annee}-${mois}`} className="flex flex-col gap-1.5">
              <div className={`text-xs font-semibold pb-1.5 border-b ${
                isCurrent ? 'text-blue-600 border-blue-200' : 'text-gray-400 border-gray-100'
              }`}>
                {MOIS_NOMS_COURTS[mois]}
                {annee !== aujourd.getFullYear() && <span className="ml-1 font-normal">{annee}</span>}
                {isCurrent && <span className="ml-1 font-normal text-blue-400">· now</span>}
              </div>
              {chips.length === 0 ? (
                <p className="text-xs text-gray-300 py-1">—</p>
              ) : (
                chips.map((ech) => {
                  const jr = ech.joursRestants ?? 999;
                  return (
                    <button
                      key={ech.id}
                      onClick={() => onChipClick(ech)}
                      className={`text-left rounded-md border px-2 py-1.5 text-xs font-medium leading-snug transition-colors ${getChipClasses(jr)}`}
                      title={`${ech.titre} · ${formatJours(jr)}`}
                    >
                      <span className="flex items-start gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1 ${getDotClass(jr, false)}`} />
                        <span className="line-clamp-2">{ech.titre}</span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 flex-wrap mt-3 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">Légende :</span>
        {[
          { dot: 'bg-red-500', label: 'Dépassé / critique' },
          { dot: 'bg-amber-500', label: 'Dans les 30j' },
          { dot: 'bg-blue-400', label: 'Planifié' },
        ].map(({ dot, label }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT : LISTE PRIORITAIRE (dépassées + <30j)
// ============================================

interface ListePrioritaireProps {
  echeances: Echeance[];
  onToggleResolu: (ech: Echeance) => void;
  onModifier: (ech: Echeance) => void;
  onSupprimer: (id: string) => void;
  onVoirSource: (ech: Echeance) => void;
  onRelancer: (ech: Echeance) => void;
}

const ListePrioritaire: React.FC<ListePrioritaireProps> = ({
  echeances,
  onToggleResolu,
  onModifier,
  onSupprimer,
  onVoirSource,
  onRelancer,
}) => {
  const urgentes = useMemo(() =>
    echeances
      .filter((e) => !e.terminee && !e.resolu && (e.joursRestants ?? 999) <= 30)
      .sort((a, b) => (a.joursRestants ?? 999) - (b.joursRestants ?? 999)),
    [echeances]
  );

  if (urgentes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
        <p className="text-sm font-medium text-gray-700">Rien d'urgent dans les 30 prochains jours</p>
        <p className="text-xs text-gray-400 mt-1">SENTINELLE surveille pour toi</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {urgentes.map((ech, idx) => {
        const jr = ech.joursRestants ?? 999;
        const dateEch = getDateEcheance(ech);
        const isManuelle = ech.source === 'manuelle';
        const sc = SOURCE_CONFIG[ech.source];

        return (
          <div
            key={ech.id}
            className={`flex items-center gap-3 px-4 py-3 ${
              idx < urgentes.length - 1 ? 'border-b border-gray-100' : ''
            }`}
          >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotClass(jr, false)}`} />

            <div className="min-w-[80px]">
              {jr < 0 ? (
                <span className="text-xs font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                  {formatJours(jr)}
                </span>
              ) : (
                <span className="text-xs text-gray-500">
                  {dateEch.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{ech.titre}</p>
              {ech.description && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{ech.description}</p>
              )}
            </div>

            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getBadgeClasses(ech.source)}`}>
              {sc.icon}
              {sc.label}
            </span>

            <div className="flex items-center gap-1 flex-shrink-0">
              {ech.source === 'courrier' && ech.sourceId && (
                <button
                  onClick={() => onRelancer(ech)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Relancer
                </button>
              )}
              {ech.sourceId && ech.source !== 'garantie' && (
                <button
                  onClick={() => onVoirSource(ech)}
                  className="px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50 transition-colors"
                  title="Voir"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => onToggleResolu(ech)}
                className="px-2 py-1 rounded text-xs text-green-700 hover:bg-green-50 transition-colors"
                title="Clore"
              >
                <CheckCheck className="w-3.5 h-3.5" />
              </button>
              {isManuelle && (
                <button onClick={() => onModifier(ech)} className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 transition-colors" title="Modifier">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
              )}
              {isManuelle && (
                <button onClick={() => onSupprimer(ech.id)} className="px-2 py-1 rounded text-xs text-red-500 hover:bg-red-50 transition-colors" title="Supprimer">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// COMPOSANT : TOUTES LES ÉCHÉANCES (accordéon)
// ============================================

interface ToutesEcheancesProps {
  echeances: Echeance[];
  filtreSource: EcheanceSource | 'toutes';
  onFiltreSourceChange: (v: EcheanceSource | 'toutes') => void;
  masquerTerminees: boolean;
  onMasquerTermineesChange: (v: boolean) => void;
  onToggleResolu: (ech: Echeance) => void;
  onModifier: (ech: Echeance) => void;
  onSupprimer: (id: string) => void;
  onVoirSource: (ech: Echeance) => void;
  onRelancer: (ech: Echeance) => void;
}

const ToutesEcheances: React.FC<ToutesEcheancesProps> = ({
  echeances,
  filtreSource,
  onFiltreSourceChange,
  masquerTerminees,
  onMasquerTermineesChange,
  onToggleResolu,
  onModifier,
  onSupprimer,
  onVoirSource,
  onRelancer,
}) => {
  const [ouvert, setOuvert] = useState(false);

  const echeancesFiltrees = useMemo(() => {
    let r = echeances;
    if (filtreSource !== 'toutes') r = r.filter((e) => e.source === filtreSource);
    if (masquerTerminees) r = r.filter((e) => !e.terminee && !e.resolu);
    return r.sort((a, b) => (a.joursRestants ?? 999) - (b.joursRestants ?? 999));
  }, [echeances, filtreSource, masquerTerminees]);

  const totalActives = echeances.filter((e) => !e.terminee && !e.resolu).length;

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-700">Toutes les échéances ({totalActives})</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 flex-wrap">
        <span className="text-sm font-semibold text-gray-800">
          Toutes les échéances ({echeancesFiltrees.length})
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filtreSource}
            onChange={(e) => onFiltreSourceChange(e.target.value as EcheanceSource | 'toutes')}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="toutes">Toutes les sources</option>
            <option value="garantie">Garanties</option>
            <option value="courrier">Courriers</option>
            <option value="dossier">Dossiers</option>
            <option value="manuelle">Manuelles</option>
          </select>
          <button
            onClick={() => onMasquerTermineesChange(!masquerTerminees)}
            className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${
              masquerTerminees ? 'text-gray-500 hover:bg-gray-100' : 'text-amber-600 bg-amber-50'
            }`}
          >
            {masquerTerminees ? 'Voir closes' : 'Masquer closes'}
          </button>
          <button
            onClick={() => setOuvert(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title="Réduire"
          >
            <ChevronRight className="w-4 h-4 text-gray-400 rotate-90" />
          </button>
        </div>
      </div>

      {echeancesFiltrees.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">Aucune échéance dans cette sélection.</p>
      ) : (
        echeancesFiltrees.map((ech, idx) => {
          const jr = ech.joursRestants ?? 999;
          const isManuelle = ech.source === 'manuelle';
          const isResolu = ech.resolu === true;
          const dateEch = getDateEcheance(ech);
          const sc = SOURCE_CONFIG[ech.source];

          return (
            <div
              key={ech.id}
              className={`flex items-center gap-3 px-4 py-3 ${
                idx < echeancesFiltrees.length - 1 ? 'border-b border-gray-100' : ''
              } ${isResolu ? 'opacity-50' : ''}`}
            >
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotClass(jr, isResolu)}`} />

              <div className="min-w-[90px]">
                <span className="text-xs text-gray-500">
                  {dateEch.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' })}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isResolu ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                  {ech.titre}
                </p>
              </div>

              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getBadgeClasses(ech.source)}`}>
                {sc.icon}
                {sc.label}
              </span>

              {!isResolu && (
                <span className="text-xs text-gray-500 min-w-[52px] text-right">{formatJours(jr)}</span>
              )}

              <div className="flex items-center gap-1 flex-shrink-0">
                {ech.source === 'courrier' && ech.sourceId && !isResolu && (
                  <button
                    onClick={() => onRelancer(ech)}
                    className="px-2 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    title="Relancer SENTINELLE"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                  </button>
                )}
                {ech.sourceId && ech.source !== 'garantie' && (
                  <button onClick={() => onVoirSource(ech)} className="px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50 transition-colors" title="Voir">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => onToggleResolu(ech)}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    isResolu ? 'text-amber-600 hover:bg-amber-50' : 'text-green-700 hover:bg-green-50'
                  }`}
                  title={isResolu ? 'Rouvrir' : 'Clore'}
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                </button>
                {isManuelle && (
                  <button onClick={() => onModifier(ech)} className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 transition-colors" title="Modifier">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                )}
                {isManuelle && (
                  <button onClick={() => onSupprimer(ech.id)} className="px-2 py-1 rounded text-xs text-red-500 hover:bg-red-50 transition-colors" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ============================================
// COMPOSANT : MODAL CRÉATION / ÉDITION
// ============================================

interface ModalEcheanceProps {
  echeance?: Echeance | null;
  coproprieteId: string;
  onClose: () => void;
  onSaved: () => void;
}

const ModalEcheance: React.FC<ModalEcheanceProps> = ({ echeance, coproprieteId, onClose, onSaved }) => {
  const isEdition = !!echeance;
  const [titre, setTitre] = useState(echeance?.titre || '');
  const [description, setDescription] = useState(echeance?.description || '');
  const [categorie, setCategorie] = useState<EcheanceCategorieManuelle>(echeance?.categorie || 'personnalise');
  const [dateStr, setDateStr] = useState(() => {
    if (echeance?.dateEcheance) {
      const d = echeance.dateEcheance.toDate?.() || new Date(echeance.dateEcheance as any);
      return d.toISOString().split('T')[0];
    }
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const previewUrgence = useMemo(() => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const jours = calculerJoursRestants(date);
    const urgence = calculerUrgence(jours);
    return { jours, urgence, config: ECHEANCE_URGENCE_CONFIG[urgence] };
  }, [dateStr]);

  const handleSubmit = async () => {
    if (!titre.trim()) { setErreur('Le titre est obligatoire.'); return; }
    if (!dateStr) { setErreur("La date d'échéance est obligatoire."); return; }
    const dateEch = new Date(dateStr);
    if (isNaN(dateEch.getTime())) { setErreur('Date invalide.'); return; }
    setErreur('');
    setSaving(true);
    try {
      if (isEdition && echeance) {
        await modifierEcheance(echeance.id, {
          titre: titre.trim(),
          description: description.trim() || undefined,
          categorie,
          dateEcheance: Timestamp.fromDate(dateEch),
        });
      } else {
        await creerEcheance({
          coproprieteId,
          titre: titre.trim(),
          description: description.trim() || undefined,
          dateEcheance: Timestamp.fromDate(dateEch),
          source: 'manuelle',
          categorie,
          terminee: false,
        });
      }
      onSaved();
    } catch (error) {
      console.error('[Calendrier] Erreur sauvegarde échéance:', error);
      setErreur('Erreur lors de la sauvegarde. Réessaie.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdition ? "Modifier l'échéance" : 'Nouvelle échéance'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
            <select
              value={categorie}
              onChange={(e) => setCategorie(e.target.value as EcheanceCategorieManuelle)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              {Object.entries(ECHEANCE_CATEGORIES_MANUELLES).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex : AG annuelle, Relance syndic, Renouvellement assurance..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{"Date d'échéance"}</label>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            {previewUrgence && (
              <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg ${previewUrgence.config.couleurBg} ${previewUrgence.config.couleurBorder} border`}>
                <div className={`w-2.5 h-2.5 rounded-full ${previewUrgence.config.couleurDot}`} />
                <span className={`text-xs font-semibold ${previewUrgence.config.couleurTexte}`}>
                  {formatJours(previewUrgence.jours)} — {previewUrgence.config.label}
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Contexte, documents nécessaires, actions à préparer..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            />
          </div>
          {erreur && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{erreur}</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdition ? 'Enregistrer' : "Créer l'échéance"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const CalendrierVigiePage: React.FC = () => {
  const { coproprieteId } = useCopropriete();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [toutesEcheances, setToutesEcheances] = useState<Echeance[]>([]);
  const [loading, setLoading] = useState(true);

  const [filtreSource, setFiltreSource] = useState<EcheanceSource | 'toutes'>('toutes');
  const [masquerTerminees, setMasquerTerminees] = useState(true);

  const [modalOuverte, setModalOuverte] = useState(false);
  const [echeanceEnEdition, setEcheanceEnEdition] = useState<Echeance | null>(null);
  const [suppressionId, setSuppressionId] = useState<string | null>(null);

  const [aideAccueilVue, setAideAccueilVue] = useState(true);
  const [aideAccueilChargee, setAideAccueilChargee] = useState(false);
  const [afficherAideComplete, setAfficherAideComplete] = useState(false);

  // Rétrocompat session 180 : nettoyer paramètre ?vue=urgences
  useEffect(() => {
    if (searchParams.get('vue')) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Aide contextuelle
  useEffect(() => {
    const chargerPreferenceAide = async () => {
      if (!userProfile?.id) return;
      try {
        const prefRef = doc(db, 'userPreferences', userProfile.id);
        const prefSnap = await getDoc(prefRef);
        if (prefSnap.exists()) {
          const data = prefSnap.data();
          setAideAccueilVue(data?.aideVue?.calendrier === true);
        } else {
          setAideAccueilVue(false);
        }
      } catch {
        setAideAccueilVue(false);
      } finally {
        setAideAccueilChargee(true);
      }
    };
    chargerPreferenceAide();
  }, [userProfile?.id]);

  const fermerAideAccueil = async () => {
    setAideAccueilVue(true);
    setAfficherAideComplete(false);
    if (userProfile?.id) {
      try {
        const prefRef = doc(db, 'userPreferences', userProfile.id);
        await setDoc(prefRef, { aideVue: { calendrier: true } }, { merge: true });
      } catch {
        // silencieux
      }
    }
  };

  // Chargement données
  const chargerDonnees = async () => {
    if (!coproprieteId) return;
    setLoading(true);
    try {
      const data = await chargerToutesEcheances(coproprieteId);
      setToutesEcheances(data);
    } catch (error) {
      console.error('[Calendrier] Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    chargerDonnees();
  }, [coproprieteId]);

  // Résumé compteurs
  const resume = useMemo(() => {
    const nonTerminees = toutesEcheances.filter((e) => !e.terminee && !e.resolu);
    const depassees = nonTerminees.filter((e) => (e.joursRestants ?? 0) < 0).length;
    const urgentes = nonTerminees.filter((e) => {
      const jr = e.joursRestants ?? 999;
      return jr >= 0 && jr <= 30;
    }).length;
    const aSurveiller = nonTerminees.filter((e) => (e.joursRestants ?? 999) > 30).length;
    return { total: nonTerminees.length, depassees, urgentes, aSurveiller };
  }, [toutesEcheances]);

  // Actions
  const handleToggleResolu = async (echeance: Echeance) => {
    const newResolu = !echeance.resolu;
    try {
      if (echeance.source === 'manuelle') {
        await updateDoc(doc(db, 'echeances', echeance.id), { resolu: newResolu });
      } else if (echeance.source === 'courrier' && echeance.sourceId) {
        await updateDoc(doc(db, 'courriers', echeance.sourceId), { resolu: newResolu });
      } else if (echeance.source === 'dossier' && echeance.sourceId) {
        await updateDoc(doc(db, 'affaires', echeance.sourceId), { resolu: newResolu });
      }
      await chargerDonnees();
    } catch (error) {
      console.error('[Calendrier] Erreur toggle résolu:', error);
    }
  };

  const handleModifier = (echeance: Echeance) => {
    setEcheanceEnEdition(echeance);
    setModalOuverte(true);
  };

  const handleSupprimer = async (id: string) => {
    try {
      await supprimerEcheance(id);
      setSuppressionId(null);
      await chargerDonnees();
    } catch (error) {
      console.error('[Calendrier] Erreur suppression:', error);
    }
  };

  const handleVoirSource = (echeance: Echeance) => {
    if (!echeance.sourceId) return;
    if (echeance.source === 'courrier') navigate(`/vigie/courriers/${echeance.sourceId}`);
    else if (echeance.source === 'dossier') navigate(`/vigie/dossiers/${echeance.sourceId}`);
  };

  const handleSaved = async () => {
    setModalOuverte(false);
    setEcheanceEnEdition(null);
    await chargerDonnees();
  };

  const handleRelancer = (echeance: Echeance) => {
    if (!echeance.sourceId) return;
    navigate(`/vigie/sentinelle?contexte=relance&courrierId=${echeance.sourceId}&titre=${encodeURIComponent(echeance.titre)}`);
  };

  const handleNouvelleEcheance = () => {
    setEcheanceEnEdition(null);
    setModalOuverte(true);
  };

  // Clic chip frise : si courrier urgent → relancer, sinon → voir source
  const handleChipClick = (ech: Echeance) => {
    const jr = ech.joursRestants ?? 999;
    if (ech.source === 'courrier' && ech.sourceId && jr <= 30) {
      handleRelancer(ech);
    } else if (ech.sourceId && ech.source !== 'garantie') {
      handleVoirSource(ech);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading || !aideAccueilChargee) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-3" />
          <p className="text-gray-500">Chargement des échéances...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* EN-TÊTE */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mes échéances</h1>
              <p className="text-sm text-gray-500">Frise 6 mois — toutes sources confondues</p>
            </div>
          </div>
          <button
            onClick={handleNouvelleEcheance}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle échéance
          </button>
        </div>
      </div>

      {/* AIDE CONTEXTUELLE */}
      {(!aideAccueilVue || afficherAideComplete) ? (
        <AideAccueil onFermer={fermerAideAccueil} />
      ) : (
        <AideCompacte onVoirPlus={() => setAfficherAideComplete(true)} />
      )}

      {/* COMPTEURS */}
      {resume.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{resume.total}</p>
            <p className="text-xs text-gray-500">Actives</p>
          </div>
          <div className={`rounded-xl border p-4 text-center ${resume.depassees > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
            <p className={`text-2xl font-bold ${resume.depassees > 0 ? 'text-red-600' : 'text-gray-400'}`}>{resume.depassees}</p>
            <p className={`text-xs ${resume.depassees > 0 ? 'text-red-500' : 'text-gray-500'}`}>Dépassées</p>
          </div>
          <div className={`rounded-xl border p-4 text-center ${resume.urgentes > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
            <p className={`text-2xl font-bold ${resume.urgentes > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{resume.urgentes}</p>
            <p className={`text-xs ${resume.urgentes > 0 ? 'text-amber-500' : 'text-gray-500'}`}>Urgentes (&lt;30j)</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{resume.aSurveiller}</p>
            <p className="text-xs text-gray-500">À surveiller</p>
          </div>
        </div>
      )}

      {/* FRISE 6 MOIS */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Vue 6 mois</p>
        <Frise6Mois echeances={toutesEcheances} onChipClick={handleChipClick} />
      </div>

      {/* LISTE PRIORITAIRE */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
          Priorité immédiate — dépassées et 30 prochains jours
        </p>
        <ListePrioritaire
          echeances={toutesEcheances}
          onToggleResolu={handleToggleResolu}
          onModifier={handleModifier}
          onSupprimer={(id) => setSuppressionId(id)}
          onVoirSource={handleVoirSource}
          onRelancer={handleRelancer}
        />
      </div>

      {/* TOUTES LES ÉCHÉANCES (accordéon) */}
      <ToutesEcheances
        echeances={toutesEcheances}
        filtreSource={filtreSource}
        onFiltreSourceChange={setFiltreSource}
        masquerTerminees={masquerTerminees}
        onMasquerTermineesChange={setMasquerTerminees}
        onToggleResolu={handleToggleResolu}
        onModifier={handleModifier}
        onSupprimer={(id) => setSuppressionId(id)}
        onVoirSource={handleVoirSource}
        onRelancer={handleRelancer}
      />

      {/* MODAL CRÉATION / ÉDITION */}
      {modalOuverte && coproprieteId && (
        <ModalEcheance
          echeance={echeanceEnEdition}
          coproprieteId={coproprieteId}
          onClose={() => { setModalOuverte(false); setEcheanceEnEdition(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* MODAL CONFIRMATION SUPPRESSION */}
      {suppressionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSuppressionId(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Supprimer cette échéance ?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Cette action est irréversible.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setSuppressionId(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Annuler
              </button>
              <button
                onClick={() => handleSupprimer(suppressionId)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendrierVigiePage;
